import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';

const StarRow = ({ value }) => (
  <span className="text-amber-500 text-sm tracking-tight" aria-label={`${value} estrellas`}>
    {'★'.repeat(value)}<span className="text-gray-300">{'★'.repeat(5 - value)}</span>
  </span>
);

const FeedbackSoportePanel = () => {
  const { API } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], stats: { total: 0, promedio: 0, distribucion: {}, ranking_admins: [] } });
  const [loading, setLoading] = useState(false);
  const [filterStars, setFilterStars] = useState(0); // 0 = all

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/feedback-soporte`);
      setData(r.data || { items: [], stats: { total: 0, promedio: 0, distribucion: {}, ranking_admins: [] } });
    } catch {
      toast.error('No se pudo cargar el feedback');
    } finally {
      setLoading(false);
    }
  };

  const deleteOne = async (id, nombre) => {
    if (!window.confirm(`¿Borrar el feedback de "${nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/feedback-soporte/${id}`);
      toast.success('Feedback eliminado');
      load();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const deleteOlder = async () => {
    const dias = window.prompt('Borrar todos los feedbacks de más de cuántos días? (default: 30)', '30');
    if (!dias) return;
    const n = parseInt(dias, 10);
    if (isNaN(n) || n < 1) { toast.error('Número inválido'); return; }
    if (!window.confirm(`¿Confirmás borrar TODOS los feedbacks con más de ${n} días? Esta acción no se puede deshacer.`)) return;
    try {
      const r = await axios.delete(`${API}/admin/feedback-soporte`, { params: { dias: n } });
      toast.success(`Limpieza OK: ${r.data.citas_borradas} citas + ${r.data.tickets_limpiados} tickets`);
      load();
    } catch {
      toast.error('No se pudo limpiar');
    }
  };

  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const items = filterStars > 0
    ? data.items.filter(i => i.calificacion === filterStars)
    : data.items;

  const stats = data.stats || { total: 0, promedio: 0, distribucion: {}, ranking_admins: [] };
  const maxBar = Math.max(...Object.values(stats.distribucion || {}), 1);

  return (
    <div className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="feedback-soporte-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-amber-100/50 rounded-2xl transition"
        data-testid="feedback-panel-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
            <i className="fas fa-star text-white" />
          </div>
          <div className="text-left">
            <div className="font-bold text-gray-900">Feedback de Soporte</div>
            <div className="text-xs text-gray-500">
              {stats.total} calificación{stats.total === 1 ? '' : 'es'} · Promedio <b className="text-amber-600">{stats.promedio || 0}★</b>
            </div>
          </div>
        </div>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} text-amber-600`} />
      </button>

      {open && (
        <div className="p-4 pt-0 space-y-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              <i className="fas fa-spinner fa-spin text-2xl mb-2" />
              <div>Cargando...</div>
            </div>
          ) : (
            <>
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Distribución */}
                <div className="bg-white rounded-xl p-4 border border-amber-100">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">Distribución</div>
                  {[5, 4, 3, 2, 1].map(n => {
                    const count = stats.distribucion?.[String(n)] || 0;
                    const pct = (count / maxBar) * 100;
                    return (
                      <div key={n} className="flex items-center gap-2 mb-1">
                        <button
                          onClick={() => setFilterStars(filterStars === n ? 0 : n)}
                          className={`text-xs w-12 text-left flex items-center gap-0.5 ${filterStars === n ? 'font-bold text-amber-600' : 'text-gray-600 hover:text-amber-600'}`}
                          data-testid={`filter-stars-${n}`}
                        >
                          {n}<i className="fas fa-star text-amber-500 text-xs" />
                        </button>
                        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-xs text-gray-600 w-8 text-right tabular-nums">{count}</div>
                      </div>
                    );
                  })}
                  {filterStars > 0 && (
                    <button
                      onClick={() => setFilterStars(0)}
                      className="text-xs text-amber-600 hover:underline mt-2"
                      data-testid="clear-filter-btn"
                    >
                      Limpiar filtro
                    </button>
                  )}
                </div>

                {/* Ranking admins */}
                <div className="bg-white rounded-xl p-4 border border-amber-100">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">Ranking Admins</div>
                  {(stats.ranking_admins || []).length === 0 && (
                    <div className="text-xs text-gray-400 italic">Sin datos aún</div>
                  )}
                  {(stats.ranking_admins || []).slice(0, 5).map((a, idx) => (
                    <div key={a.email} className="flex items-center gap-2 py-1 text-sm">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${idx === 0 ? 'bg-yellow-400 text-white' : idx === 1 ? 'bg-gray-300 text-white' : idx === 2 ? 'bg-amber-700 text-white' : 'bg-gray-100 text-gray-600'}`}>
                        {idx + 1}
                      </span>
                      <span className="flex-1 truncate text-gray-700" title={a.email}>{a.nombre}</span>
                      <span className="font-bold text-amber-600 tabular-nums">{a.promedio}★</span>
                      <span className="text-xs text-gray-400">({a.total})</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lista */}
              <div className="bg-white rounded-xl border border-amber-100 max-h-96 overflow-y-auto">
                <div className="px-4 py-2 bg-amber-50 sticky top-0 border-b border-amber-100 flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs font-bold text-gray-600 uppercase">
                    Comentarios y Calificaciones {filterStars > 0 && <span className="text-amber-600">(filtrado: {filterStars}★)</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      onClick={deleteOlder}
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs text-red-600 hover:bg-red-50"
                      data-testid="delete-older-feedback-btn"
                      title="Borrar feedbacks antiguos"
                    >
                      <i className="fas fa-broom mr-1" />Limpiar antiguos
                    </Button>
                    <Button onClick={load} variant="ghost" size="sm" className="h-6 text-xs" data-testid="refresh-feedback-btn">
                      <i className="fas fa-sync-alt mr-1" />Refrescar
                    </Button>
                  </div>
                </div>
                {items.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    <i className="fas fa-inbox text-2xl mb-2 block" />
                    Aún no hay feedback {filterStars > 0 ? 'con esa calificación' : ''}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {items.map(it => (
                      <div key={it.id} className="p-3 group" data-testid={`feedback-item-${it.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <StarRow value={it.calificacion} />
                            <span className="text-sm font-semibold text-gray-800">{it.usuario_nombre || 'Usuario'}</span>
                            {it.es_cita && (
                              <span className="text-[10px] bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded font-bold">CITA</span>
                            )}
                            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{it.categoria}</span>
                          </div>
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-[10px] text-gray-400">
                              {it.fecha ? new Date(it.fecha).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                            </span>
                            <button
                              onClick={() => deleteOne(it.id, it.usuario_nombre || 'Usuario')}
                              className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition p-1"
                              title="Borrar este feedback"
                              data-testid={`delete-feedback-${it.id}`}
                            >
                              <i className="fas fa-trash-alt text-xs" />
                            </button>
                          </div>
                        </div>
                        {it.comentario && (
                          <div className="bg-gray-50 rounded-lg px-3 py-2 mt-1 text-sm text-gray-700 italic border-l-2 border-amber-400">
                            "{it.comentario}"
                          </div>
                        )}
                        <div className="text-[11px] text-gray-400 mt-1">
                          Atendido por: <b>{it.respondido_por_nombre || it.respondido_por}</b>
                          {it.descripcion && (
                            <span className="ml-2">· Original: <em className="text-gray-500">{it.descripcion.slice(0, 80)}{it.descripcion.length > 80 ? '...' : ''}</em></span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackSoportePanel;
